import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface DataPoint {
  id: number;
  quality: number;
  cost: number;
}

const DATA: DataPoint[] = [
  { id: 1,  quality: 9.8, cost: 10200 },
  { id: 2,  quality: 9.5, cost: 10900 },
  { id: 3,  quality: 9.2, cost: 11100 },
  { id: 4,  quality: 9.1, cost: 10400 },
  { id: 5,  quality: 9.6, cost: 11300 },
  { id: 6,  quality: 9.7, cost: 14100 },
  { id: 7,  quality: 9.4, cost: 13700 },
  { id: 8,  quality: 9.3, cost: 14900 },
  { id: 9,  quality: 8.7, cost: 12800 },
  { id: 10, quality: 8.5, cost: 13400 },
  { id: 11, quality: 8.8, cost: 13100 },
  { id: 12, quality: 8.3, cost: 11500 },
  { id: 13, quality: 8.6, cost: 12300 },
  { id: 14, quality: 8.4, cost: 14200 },
  { id: 15, quality: 7.8, cost: 9800  },
  { id: 16, quality: 7.5, cost: 10100 },
  { id: 17, quality: 7.9, cost: 11800 },
  { id: 18, quality: 8.0, cost: 12500 },
  { id: 19, quality: 7.6, cost: 13200 },
  { id: 20, quality: 8.1, cost: 14600 },
];

const SWEET_SPOT_MIN_Q = 9.0;
const SWEET_SPOT_MAX_C = 11500;

function isSweet(d: DataPoint) {
  return d.quality >= SWEET_SPOT_MIN_Q && d.cost <= SWEET_SPOT_MAX_C;
}

// Chart layout
const VW = 700;
const VH = 460;
const PAD = { top: 52, right: 44, bottom: 64, left: 78 };
const PW = VW - PAD.left - PAD.right;
const PH = VH - PAD.top - PAD.bottom;

const Q_MIN = 7.0;
const Q_MAX = 10.5;
const C_MIN = 8000;
const C_MAX = 16000;

function sx(q: number) { return PAD.left + ((q - Q_MIN) / (Q_MAX - Q_MIN)) * PW; }
function sy(c: number) { return PAD.top + (1 - (c - C_MIN) / (C_MAX - C_MIN)) * PH; }

const CENTER_X = PAD.left + PW / 2;

const Y_TICKS = [8000, 9000, 10000, 11000, 12000, 13000, 14000, 15000, 16000];
const X_TICKS = [7.0, 7.5, 8.0, 8.5, 9.0, 9.5, 10.0, 10.5];

type Phase = 'initial' | 'animating' | 'revealed';

export default function RevealChart() {
  const [phase, setPhase] = useState<Phase>('initial');
  const [showQuadrant, setShowQuadrant] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [showXAxis, setShowXAxis] = useState(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => timeoutsRef.current.forEach(clearTimeout);
  }, []);

  function handleReveal() {
    if (phase !== 'initial') return;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    setPhase('animating');

    const t1 = setTimeout(() => setShowXAxis(true), 80);
    const t2 = setTimeout(() => setPhase('revealed'), 1600);
    const t3 = setTimeout(() => setShowQuadrant(true), 2000);
    const t4 = setTimeout(() => setShowMessage(true), 2300);
    timeoutsRef.current = [t1, t2, t3, t4];
  }

  const revealed = phase === 'revealed' || phase === 'animating';

  // Quadrant bounds
  const qx1 = sx(SWEET_SPOT_MIN_Q);
  const qx2 = sx(Q_MAX);
  const qy1 = sy(SWEET_SPOT_MAX_C);
  const qy2 = sy(C_MIN);

  return (
    <div className="w-full flex flex-col items-center gap-6">

      {/* Phase Label */}
      <div className="relative h-12 w-full flex items-center justify-center">
        <div
          className="absolute inset-0 flex items-center justify-center transition-all duration-500"
          style={{ opacity: phase === 'initial' ? 1 : 0, transform: phase === 'initial' ? 'translateY(0)' : 'translateY(-8px)' }}
        >
          <span className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-5 py-2 text-sm font-semibold text-slate-600">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
            What You See: Price Alone
          </span>
        </div>
        <div
          className="absolute inset-0 flex items-center justify-center transition-all duration-500"
          style={{ opacity: phase !== 'initial' ? 1 : 0, transform: phase !== 'initial' ? 'translateY(0)' : 'translateY(8px)' }}
        >
          <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-5 py-2 text-sm font-semibold text-emerald-700">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
            What We See: Quality + Price Together
          </span>
        </div>
      </div>

      {/* Chart SVG */}
      <div className="w-full max-w-3xl" style={{ aspectRatio: `${VW} / ${VH}` }}>
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          className="w-full h-full"
          style={{ overflow: 'visible' }}
        >
          {/* Sweet spot quadrant highlight */}
          <rect
            x={qx1}
            y={qy1}
            width={qx2 - qx1}
            height={qy2 - qy1}
            fill="rgba(22,163,74,0.07)"
            stroke="#16a34a"
            strokeWidth={2}
            strokeDasharray="6 3"
            rx={6}
            style={{
              opacity: showQuadrant ? 1 : 0,
              transition: 'opacity 0.6s ease',
            }}
          />
          {/* Sweet spot label */}
          <text
            x={qx1 + (qx2 - qx1) / 2}
            y={qy1 - 10}
            textAnchor="middle"
            fontSize="11"
            fontWeight="700"
            fill="#16a34a"
            style={{ opacity: showQuadrant ? 1 : 0, transition: 'opacity 0.6s ease 0.2s' }}
          >
            Sweet Spot
          </text>

          {/* Y-axis grid + labels — always visible */}
          {Y_TICKS.map(t => (
            <g key={t}>
              <line
                x1={PAD.left} x2={PAD.left + PW}
                y1={sy(t)} y2={sy(t)}
                stroke="#e5e7eb" strokeWidth={1}
              />
              <text
                x={PAD.left - 10}
                y={sy(t) + 4}
                textAnchor="end"
                fontSize="11"
                fill="#64748b"
              >
                ${(t / 1000).toFixed(0)}k
              </text>
            </g>
          ))}

          {/* X-axis grid + labels — fade in on reveal */}
          <g style={{ opacity: showXAxis ? 1 : 0, transition: 'opacity 0.5s ease' }}>
            {X_TICKS.map(t => (
              <g key={t}>
                <line
                  x1={sx(t)} x2={sx(t)}
                  y1={PAD.top} y2={PAD.top + PH}
                  stroke="#e5e7eb" strokeWidth={1}
                />
                <text
                  x={sx(t)}
                  y={PAD.top + PH + 18}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#64748b"
                >
                  {t.toFixed(1)}
                </text>
              </g>
            ))}
          </g>

          {/* Axes */}
          <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + PH} stroke="#cbd5e1" strokeWidth={1.5} />
          <line x1={PAD.left} y1={PAD.top + PH} x2={PAD.left + PW} y2={PAD.top + PH} stroke="#cbd5e1" strokeWidth={1.5} />

          {/* Y-axis title */}
          <text
            x={-(PAD.top + PH / 2)}
            y={20}
            textAnchor="middle"
            fontSize="12"
            fontWeight="600"
            fill="#374151"
            transform="rotate(-90)"
          >
            Average Cost ($)
          </text>

          {/* X-axis title — fades in */}
          <text
            x={PAD.left + PW / 2}
            y={VH - 6}
            textAnchor="middle"
            fontSize="12"
            fontWeight="600"
            fill="#374151"
            style={{ opacity: showXAxis ? 1 : 0, transition: 'opacity 0.5s ease' }}
          >
            Quality Score (0–10)
          </text>

          {/* Initial single-line guide — fades out on reveal */}
          <g style={{ opacity: phase === 'initial' ? 1 : 0, transition: 'opacity 0.4s ease' }}>
            <line
              x1={CENTER_X} x2={CENTER_X}
              y1={PAD.top} y2={PAD.top + PH}
              stroke="#3b82f6"
              strokeWidth={2}
              strokeDasharray="4 3"
              opacity={0.4}
            />
            <text
              x={CENTER_X}
              y={PAD.top - 6}
              textAnchor="middle"
              fontSize="11"
              fill="#3b82f6"
              fontWeight="600"
            >
              Price Only
            </text>
          </g>

          {/* Data dots */}
          {DATA.map((d, i) => {
            const targetX = sx(d.quality);
            const startX = CENTER_X;
            const currentX = revealed ? targetX : startX;
            const sweet = isSweet(d);
            const dotColor = (phase === 'revealed' && sweet) ? '#16a34a' : '#3b82f6';
            const delay = i * 55;
            return (
              <circle
                key={d.id}
                r={7}
                cy={sy(d.cost)}
                cx={currentX}
                fill={dotColor}
                style={{
                  transition: `cx ${0.9}s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms, fill 0.4s ease ${Math.max(0, delay + 600)}ms`,
                }}
              />
            );
          })}

          {/* Legend */}
          <g style={{ opacity: phase !== 'initial' ? 1 : 0, transition: 'opacity 0.6s ease' }}>
            <circle cx={PAD.left + 10} cy={PAD.top + 18} r={6} fill="#16a34a" />
            <text x={PAD.left + 22} y={PAD.top + 22} fontSize="11" fontWeight="600" fill="#16a34a">
              OverUnder vetted bids
            </text>
            <circle cx={PAD.left + 190} cy={PAD.top + 18} r={6} fill="#3b82f6" />
            <text x={PAD.left + 202} y={PAD.top + 22} fontSize="11" fontWeight="600" fill="#3b82f6">
              No advocate bids
            </text>
          </g>
        </svg>
      </div>

      {/* CTA Button */}
      {phase === 'initial' && (
        <button
          onClick={handleReveal}
          className="inline-flex items-center gap-3 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold text-base sm:text-lg px-8 py-4 rounded-2xl cursor-pointer whitespace-nowrap transition-all duration-200"
          style={{ boxShadow: '0 6px 24px rgba(22,163,74,0.35)' }}
        >
          <i className="ri-eye-line text-xl" />
          Reveal the Full Picture – Click Here
        </button>
      )}

      {/* Insight message */}
      <div
        className="max-w-2xl w-full text-center transition-all duration-700"
        style={{ opacity: showMessage ? 1 : 0, transform: showMessage ? 'translateY(0)' : 'translateY(12px)' }}
      >
        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-7 py-5">
          <p className="text-slate-700 text-sm sm:text-base leading-relaxed font-medium">
            This is why random bids are still a gamble.{' '}
            <span className="text-emerald-700 font-bold">We see the complete quality + price picture</span>{' '}
            so you don&apos;t overpay or risk poor work.
          </p>
        </div>
      </div>

      {/* Legend row + link */}
      <div className="flex flex-col sm:flex-row items-center justify-between w-full max-w-3xl gap-4 pt-2">
        <div
          className="flex items-center gap-5 transition-all duration-500"
          style={{ opacity: phase !== 'initial' ? 1 : 0.3 }}
        >
          <span className="flex items-center gap-2 text-sm text-slate-600">
            <span className="inline-block w-3 h-3 rounded-full bg-emerald-600" />
            OverUnder vetted bids
          </span>
          <span className="flex items-center gap-2 text-sm text-slate-600">
            <span className="inline-block w-3 h-3 rounded-full bg-blue-500" />
            No advocate bids
          </span>
        </div>
        <Link
          to="/wake-county-simulation"
          className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 underline underline-offset-2 cursor-pointer whitespace-nowrap transition-colors"
        >
          Explore Full Wake County Market Data &rarr;
        </Link>
      </div>
    </div>
  );
}
